import React from 'react';
import { faChevronRight, faMessageQuestion } from '@fortawesome/pro-regular-svg-icons';
import { faClock, faRightLeftLarge, faStar, faUserClock } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import GenericSearchBar from '../../components/GenericSearchBar';
import { ExpertPlatformIndex } from './IExpertPlatformIndex';
import { Link } from '../../components/ui/Link';
import ImageLayout from '../../layouts/ImageLayout';
import Breadcrumbs from '../../components/Breadcrumbs';
import Container from '../../components/ui/Container';
import Panel from '../../components/ui/Panel';
import { default as BannerImage } from '../../assets/images/banner-expertplatform.jpg';
import ChevronLink from '../../components/ui/ChevronLink';

export const ExpertPlatformIndexPage: React.FC<ExpertPlatformIndex> = ({
  questionsCount,
  questions,
  experts,
  subjects,
}) => {
  const handleSearchClick = (query: string): void => {
    window.location.href = `/expert/zoekresultaat${query ? `?zoekTerm=${query}` : ''}`;
  };

  const breadcrumbLinks = [
    { name: 'Home', linkTo: '/' },
    { name: 'Expertplatform', linkTo: '/expert' },
  ];

  return (
    <ImageLayout image={BannerImage as string} breadcrumbs={<Breadcrumbs links={breadcrumbLinks} variant="inverted" />}>
      <Container state="imageLayoutFirst" className="pb-16 md:pb-24">
        <div className="-mt-8 mb-8">
          <h1 className="mb-5">
            <span className="text-2xl md:text-3xl lg:text-4xl md:leading-loose font-bold font-avantgarde text-white drop-shadow-md mr-2.5">
              {questionsCount}
            </span>
            <span className="text-2xl md:text-3xl lg:text-4xl md:leading-loose font-avantgarde text-white drop-shadow-md">
              Vragen in het<br />
              expertplatform
            </span>
          </h1>
          <div className="w-full">
            <div className="w-full flex justify-center xl:w-fit">
              <GenericSearchBar
                searchClicked={handleSearchClick}
                initialValue={''}
                title={'Zoeken op trefwoord'}
                placeholder={'Vul hier je zoekterm in'}
              />
            </div>
          </div>
        </div>
        <div className="-mx-2.5 flex flex-row flex-wrap">
          <Panel className="mx-2.5 w-[calc(100%-1.25rem)] lg:w-[calc(50%-1.25rem)] mt-2.5">
            <div className="flex gap-4 items-center mb-4">
              <FontAwesomeIcon
                icon={faClock as IconProp}
                className="text-blue-500 text-3xl"
              />
              <h2 className="panel-title--primary !mb-0">
                Recente vragen
              </h2>
            </div>

            {questions?.map(({ name, slug }) => (
              <ChevronLink key={slug} href={`/expert/home/vraagdetails/${slug}`} title={name} />
            ))}
          </Panel>
          <Panel className="mx-2.5 w-[calc(100%-1.25rem)] lg:w-[calc(50%-1.25rem)] mt-2.5">
            <div className="flex gap-4 items-center mb-4">
              <FontAwesomeIcon
                icon={faRightLeftLarge as IconProp}
                className="text-blue-500 text-3xl"
              />
              <h2 className="panel-title--primary !mb-0">
                Expertplatform
              </h2>
            </div>
            <h3 className="panel-title--secondary">
              Stel een vraag aan onze adviseurs
            </h3>
            <p className="font-body font-normal text-stone-800 text-[16px] leading[26.4px] mb-[48px]">
              Het expertplatform is dé plek om vragen te stellen over allerlei
              financiële onderwerpen die je niet zomaar op het internet kan
              opzoeken.<br />
              <br />
              Wellicht heb je een gerichte vraag over jouw situatie of wil je
              weten hoe bepaalde financiële regelgeving precies werkt.
            </p>
            <Link
              variant="primary"
              href="/expert/steleenvraag"
              className="w-full md:w-fit md:text-sm font-bold"
            >
              <FontAwesomeIcon icon={faMessageQuestion as IconProp} />
              Stel een vraag
              <FontAwesomeIcon icon={faChevronRight as IconProp} />
            </Link>
          </Panel>
          <Panel className="mx-2.5 w-[calc(100%-1.25rem)] lg:w-[calc(50%-1.25rem)] mt-2.5">
            <div className="flex gap-4 items-center mb-4">
              <FontAwesomeIcon
                icon={faUserClock as IconProp}
                className="text-blue-500 text-3xl"
              />
              <h2 className="panel-title--primary !mb-0">
                Recente experts
              </h2>
            </div>
            {experts?.map(({ name, slug, id }) => (
              <ChevronLink key={slug} href={`/expert/home/expert/${slug}?AdviseurId=${id}`} title={name} />
            ))}
          </Panel>
          <Panel className="mx-2.5 w-[calc(100%-1.25rem)] lg:w-[calc(50%-1.25rem)] mt-2.5">
            <div className="flex gap-4 items-center mb-4">
              <FontAwesomeIcon
                icon={faStar as IconProp}
                className="text-blue-500 text-3xl"
              />
              <h2 className="panel-title--primary !mb-0">
                Populaire onderwerpen
              </h2>
            </div>
            {subjects?.map(({ name, slug }) => (
              <ChevronLink key={slug} href={`/expert/onderwerp/index/${slug}`} title={name} />
            ))}
          </Panel>
        </div>
      </Container>
    </ImageLayout>
  );
};

ExpertPlatformIndexPage.displayName = "Page:ExpertPlatformIndex";

export default ExpertPlatformIndexPage;
